import type { Transaction } from '@elastic/apm-rum'

interface EventEntry {
    interactionId?: number
    name: string
    startTime: number
    duration: number
    processingStart: number
    processingEnd: number
    target?: Element | null
}

interface FrameScript {
    duration: number
    sourceURL?: string
    sourceFunctionName?: string
    sourceCharPosition?: number
    invoker?: string
}

interface FrameEntry {
    startTime: number
    duration: number
    scripts?: FrameScript[]
}

/** The handlers of all events painted in one frame. */
interface EventFrame {
    renderTime: number
    processingStart: number
    processingEnd: number
}

interface SlowestInteraction {
    name: string
    startTime: number
    duration: number
    target?: string
    path?: string
    frame: EventFrame
}

interface ApmObservable {
    observe?: (event: string, callback: (transaction: Transaction & { type?: string }) => void) => void
}

const SELECTOR_MAX_LENGTH = 100
const SCRIPT_LABEL_MAX_LENGTH = 200
/** Frames older than this are dropped unless they overlap the slowest interaction so far. */
const FRAME_WINDOW_MS = 10_000
/** Event Timing rounds durations to 8 ms, so entries ending this close share a frame. */
const SAME_FRAME_MS = 8
const EVENT_FRAMES_KEPT = 10

/**
 * An element and its ancestors up to the nearest one with an id, as many as fit the length cap:
 * tag, id and up to two classes each, never text or attribute values.
 */
export const inpTargetSelector = (element?: Element | null): string | undefined => {
    if (!element) return undefined
    const describe = (el: Element) => el.tagName.toLowerCase()
        + (el.id ? `#${el.id}` : '')
        + [...el.classList].slice(0, 2).map(name => `.${name}`).join('')
    let selector = describe(element).slice(0, SELECTOR_MAX_LENGTH)
    for (let el = element; !el.id && el.parentElement && !/^(?:BODY|HTML)$/.test(el.parentElement.tagName);) {
        el = el.parentElement
        const longer = `${describe(el)}>${selector}`
        if (longer.length > SELECTOR_MAX_LENGTH) break
        selector = longer
    }
    return selector
}

/** A script URL without query string or fragment, which can carry ids. */
export const stripUrlParams = (url: string): string => url.replace(/[?#].*$/, '')

/** A LoAF invoker with the URLs inside it stripped, e.g. `IMG[src=…].onload` or a script URL. */
export const stripInvokerUrls = (invoker: string): string => invoker.replace(/[\w-]+:\/\/[^\]\s]*/g, url => stripUrlParams(url))

/** `<URL> <invoker> <function>@<character position> <ms>`, the function part when the browser knows it. */
const scriptLabel = (script: FrameScript): string => {
    const at = (script.sourceCharPosition ?? -1) >= 0 ? `@${script.sourceCharPosition}` : ''
    const source = script.sourceFunctionName || at ? ` ${script.sourceFunctionName ?? ''}${at}` : ''
    return `${stripUrlParams(script.sourceURL || '') || '(no url)'} ${stripInvokerUrls(script.invoker ?? '')}${source} ${Math.round(script.duration)}`
        .slice(0, SCRIPT_LABEL_MAX_LENGTH)
}

/**
 * Keeps the slowest interaction of a page view and the long animation frames around it,
 * and turns them into APM labels: where its time went, which scripts ran and on which page.
 */
export const createInpAttribution = (hydratedAt: () => number | undefined, pathAt: (time: number) => string | undefined = () => undefined) => {
    let slowest: SlowestInteraction | undefined
    let eventFrames: EventFrame[] = []
    let frames: FrameEntry[] = []

    const overlapsSlowest = (frame: FrameEntry) => !!slowest
        && frame.startTime < slowest.startTime + slowest.duration
        && frame.startTime + frame.duration > slowest.startTime

    return {
        /** Event Timing entries, also those without an interaction: their handlers run before the same paint. */
        addEvents(entries: EventEntry[]) {
            for (const entry of entries) {
                const renderTime = entry.startTime + entry.duration
                let frame = eventFrames.find(candidate => Math.abs(candidate.renderTime - renderTime) <= SAME_FRAME_MS)
                if (frame) {
                    frame.processingStart = Math.min(frame.processingStart, entry.processingStart)
                    frame.processingEnd = Math.max(frame.processingEnd, entry.processingEnd)
                }
                else {
                    frame = { renderTime, processingStart: entry.processingStart, processingEnd: entry.processingEnd }
                    eventFrames.push(frame)
                }
                if (entry.interactionId && (!slowest || entry.duration > slowest.duration)) {
                    const { name, startTime, duration } = entry
                    // The target is read now: it can leave the DOM before the page exits.
                    slowest = { name, startTime, duration, target: inpTargetSelector(entry.target), path: pathAt(startTime), frame }
                }
            }
            eventFrames = eventFrames.filter((frame, index) => index >= eventFrames.length - EVENT_FRAMES_KEPT || frame === slowest?.frame)
        },

        addFrames(entries: FrameEntry[]) {
            frames.push(...entries)
            const newest = Math.max(...frames.map(frame => frame.startTime))
            frames = frames.filter(frame => frame.startTime >= newest - FRAME_WINDOW_MS || overlapsSlowest(frame))
        },

        labels(): Record<string, string | number | boolean> | undefined {
            if (!slowest) return undefined
            const { name, startTime, duration, target, path, frame } = slowest
            // As web-vitals: processing runs from the first handler of the frame to its last.
            const processingStart = Math.max(frame.processingStart, startTime)
            const end = Math.max(startTime + duration, processingStart)
            const processingEnd = Math.min(frame.processingEnd, end)
            const scripts = frames.filter(overlapsSlowest)
                .flatMap(frame => frame.scripts ?? [])
                .sort((a, b) => b.duration - a.duration)
                .slice(0, 3)
            const hydrated = hydratedAt()

            return {
                inp_attr_duration: Math.round(duration),
                inp_target: target ?? '(removed)',
                inp_type: name,
                inp_start: Math.round(startTime),
                inp_input_delay: Math.round(processingStart - startTime),
                inp_processing: Math.round(Math.max(0, processingEnd - processingStart)),
                inp_presentation: Math.round(Math.max(0, end - processingEnd)),
                inp_during_hydration: hydrated === undefined || startTime < hydrated,
                ...(path && { inp_path: path }),
                ...Object.fromEntries(scripts.map((script, index) => [`inp_loaf_script_${index + 1}`, scriptLabel(script)])),
            }
        },

        reset() {
            slowest = undefined
            eventFrames = []
            frames = []
        },
    }
}

/**
 * Starts collecting interactions and long animation frames, and returns a function that hands the
 * labels to the agent's page-exit transaction (the one carrying `inp_value`), starting over after
 * each, as the agent does. Undefined without Event Timing: there is nothing to attribute.
 */
export const observeInpAttribution = (hydratedAt: () => number | undefined, pathAt?: (time: number) => string | undefined): ((apm: ApmObservable | undefined) => void) | undefined => {
    const supported = typeof PerformanceObserver === 'undefined' ? [] : PerformanceObserver.supportedEntryTypes ?? []
    if (!supported.includes('event')) return undefined
    const attribution = createInpAttribution(hydratedAt, pathAt)

    // Registered first: buffered entries reach observers in registration order, so the slowest
    // interaction is known before the frames around it are pruned.
    new PerformanceObserver(list => attribution.addEvents(list.getEntries() as unknown as EventEntry[]))
        .observe({ type: 'event', durationThreshold: 40, buffered: true } as PerformanceObserverInit)
    // Without long animation frames the labels still carry the timing split.
    if (supported.includes('long-animation-frame')) {
        new PerformanceObserver(list => attribution.addFrames(list.getEntries() as unknown as FrameEntry[]))
            .observe({ type: 'long-animation-frame', buffered: true })
    }

    return (apm) => {
        apm?.observe?.('transaction:start', (transaction) => {
            if (transaction.type !== 'page-exit') return
            const labels = attribution.labels()
            if (labels) transaction.addLabels(labels)
            attribution.reset()
        })
    }
}
